import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {FaqSection, IFaqQuestion} from "@pg-mono/faq";
import {priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {IPriceReportAveragePrices} from "../types/IPriceReportAveragePrices";
import {FaqCities} from "../utils/generic_faq_cities";
import {getMedianPriceM2WithYearToYearPercent} from "../utils/get_median_price_m2";
import {getReportPeriod} from "../utils/get_report_period";
import {getPercentTrendVerb, getSignedPercentString} from "../utils/percents_handling_helpers";
import {IDistrictData} from "./prices_map/PricesMapDistricts";

interface IProps {
    minDistrict: IDistrictData | null;
    maxDistrict: IDistrictData | null;
    region: IRegionReportPrice;
}
const calculatePercentageDifference = (price1: number, price2: number) => {
    const percentageDifference = ((price1 - price2) / price2) * 100;

    return Math.round(Math.abs(percentageDifference));
};

export const PricesRegionsFAQ = (props: IProps) => {
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const prices = useSelector((state: IRPStore) => state.report.price.prices);
    const latestPriceReport = prices && prices.length > 0 ? prices[prices.length - 1] : null;

    const yearAgoPrices = useSelector((state: IRPStore) => state.report.price.yearAgoPrices) as IPriceReportAveragePrices;
    const isLower = latestPriceReport && yearAgoPrices?.avg_price > latestPriceReport?.avg_price;
    const isLowerPriceM2 = latestPriceReport && yearAgoPrices?.avg_price_m2 > latestPriceReport?.avg_price_m2;
    const avgPriceDifference = latestPriceReport && calculatePercentageDifference(yearAgoPrices?.avg_price ?? 0, latestPriceReport?.avg_price);
    const avgPriceM2Difference = latestPriceReport && calculatePercentageDifference(yearAgoPrices?.avg_price_m2 ?? 0, latestPriceReport?.avg_price_m2);
    const {minDistrict, maxDistrict, region} = props;
    const [additionalFaqs, setAdditionalFaqs] = useState<IFaqQuestion[] | null>(null);

    const getLocalGenericFaq = useCallback(
        (minDistrict: IDistrictData | null, maxDistrict: IDistrictData | null, region: IRegionReportPrice) => {
            const additionalFaqsArray: IFaqQuestion[] = [];
            const reportPeriod = maxDistrict ? getReportPeriod(maxDistrict.date) : getReportPeriod(latestPriceReport?.date);

            const {name_declension_where: regionWhere, slug: regionSlug} = region;
            const minDistrictName = minDistrict?.district || "";
            const maxDistrictName = maxDistrict?.district || "";
            const minDistrictPrice = priceFormat(minDistrict?.avg_price_m2 || 0);
            const maxDistrictPrice = priceM2Format(maxDistrict?.avg_price_m2 || 0);
            const latestAvgPriceM2 = priceFormat(latestPriceReport?.avg_price_m2 || 0);
            const latestAvgPrice = priceFormat(latestPriceReport?.avg_price || 0);

            /**
             * median paragraph appended to the m2 answer. Built once and reused for both `answer` (accordion)
             * and `answer_text` (FAQPage JSON-LD) so the two can never drift apart. Empty string when the median
             * or its year-ago counterpart is missing - no "0 zł", no empty brackets.
             */
            const median = getMedianPriceM2WithYearToYearPercent(latestPriceReport, yearAgoPrices);
            const medianAnswerPart = median
                ? ` Mediana wynosiła ${priceFormat(median.price)} za m2. To wartość środkowa, na którą nie wpływają pojedyncze bardzo drogie mieszkania. Typowa cena za metr rok do roku ${getPercentTrendVerb(
                      median.percent
                  )} (${getSignedPercentString(median.percent)}).`
                : "";
            const priceM2Answer = `W ${reportPeriod.month} ${reportPeriod.year} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. To ${avgPriceM2Difference}% ${isLowerPriceM2 ? "mniej" : "więcej"} niż w ubiegłym roku.${medianAnswerPart}`;

            if (FaqCities.includes(regionSlug)) {
                additionalFaqsArray.push(
                    {
                        name: `Która dzielnica ${regionWhere} jest najtańsza?`,
                        answer: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`,
                        answer_text: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`
                    },
                    {
                        name: `Jakie dzielnice są najdroższe ${regionWhere}?`,
                        answer: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`,
                        answer_text: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`
                    }
                );
            }

            const commonFaqsArray: IFaqQuestion[] = [
                {
                    name: `Ile kosztuje metr kwadratowy mieszkania ${regionWhere}?`,
                    answer: priceM2Answer,
                    answer_text: priceM2Answer
                },
                {
                    name: `Ile kosztuje mieszkanie ${regionWhere}?`,
                    answer: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`,
                    answer_text: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`
                }
            ];

            setAdditionalFaqs([...additionalFaqsArray, ...commonFaqsArray]);
        },
        [latestPriceReport, yearAgoPrices]
    );

    useEffect(() => {
        getLocalGenericFaq(minDistrict, maxDistrict, region);
    }, [minDistrict, maxDistrict, region]);

    return (
        <>
            {((faq && faq.length > 0) || additionalFaqs) && (
                <Container className={containerStyle}>
                    <FaqSection faqContent={faq} genericQuestions={additionalFaqs} className={faqStyle} />
                </Container>
            )}
        </>
    );
};

const containerStyle = css({
    marginBottom: 8
});

const faqStyle = css({
    "& h2": {
        textAlign: "center"
    },
    md: {
        width: "100%",
        maxWidth: "100%"
    }
});
import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {FaqSection, IFaqQuestion} from "@pg-mono/faq";
import {priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {IPriceReportAveragePrices} from "../types/IPriceReportAveragePrices";
import {FaqCities} from "../utils/generic_faq_cities";
import {getMedianPriceM2WithYearToYearPercent} from "../utils/get_median_price_m2";
import {getReportPeriod} from "../utils/get_report_period";
import {getPercentTrendVerb, getSignedPercentString} from "../utils/percents_handling_helpers";
import {IDistrictData} from "./prices_map/PricesMapDistricts";

interface IProps {
    minDistrict: IDistrictData | null;
    maxDistrict: IDistrictData | null;
    region: IRegionReportPrice;
}
const calculatePercentageDifference = (price1: number, price2: number) => {
    const percentageDifference = ((price1 - price2) / price2) * 100;

    return Math.round(Math.abs(percentageDifference));
};

export const PricesRegionsFAQ = (props: IProps) => {
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const prices = useSelector((state: IRPStore) => state.report.price.prices);
    const latestPriceReport = prices && prices.length > 0 ? prices[prices.length - 1] : null;

    const yearAgoPrices = useSelector((state: IRPStore) => state.report.price.yearAgoPrices) as IPriceReportAveragePrices;
    const isLower = latestPriceReport && yearAgoPrices?.avg_price > latestPriceReport?.avg_price;
    const isLowerPriceM2 = latestPriceReport && yearAgoPrices?.avg_price_m2 > latestPriceReport?.avg_price_m2;
    const avgPriceDifference = latestPriceReport && calculatePercentageDifference(yearAgoPrices?.avg_price ?? 0, latestPriceReport?.avg_price);
    const avgPriceM2Difference = latestPriceReport && calculatePercentageDifference(yearAgoPrices?.avg_price_m2 ?? 0, latestPriceReport?.avg_price_m2);
    const {minDistrict, maxDistrict, region} = props;
    const [additionalFaqs, setAdditionalFaqs] = useState<IFaqQuestion[] | null>(null);

    const getLocalGenericFaq = useCallback(
        (minDistrict: IDistrictData | null, maxDistrict: IDistrictData | null, region: IRegionReportPrice) => {
            const additionalFaqsArray: IFaqQuestion[] = [];
            const reportPeriod = maxDistrict ? getReportPeriod(maxDistrict.date) : getReportPeriod(latestPriceReport?.date);

            const {name_declension_where: regionWhere, slug: regionSlug} = region;
            const minDistrictName = minDistrict?.district || "";
            const maxDistrictName = maxDistrict?.district || "";
            const minDistrictPrice = priceFormat(minDistrict?.avg_price_m2 || 0);
            const maxDistrictPrice = priceM2Format(maxDistrict?.avg_price_m2 || 0);
            const latestAvgPriceM2 = priceFormat(latestPriceReport?.avg_price_m2 || 0);
            const latestAvgPrice = priceFormat(latestPriceReport?.avg_price || 0);

            /**
             * median paragraph appended to the m2 answer. Built once and reused for both `answer` (accordion)
             * and `answer_text` (FAQPage JSON-LD) so the two can never drift apart. Empty string when the median
             * or its year-ago counterpart is missing - no "0 zł", no empty brackets.
             */
            const median = getMedianPriceM2WithYearToYearPercent(latestPriceReport, yearAgoPrices);
            const medianAnswerPart = median
                ? ` Mediana wynosiła ${priceFormat(median.price)} za m2. To wartość środkowa, na którą nie wpływają pojedyncze bardzo drogie mieszkania. Typowa cena za metr rok do roku ${getPercentTrendVerb(
                      median.percent
                  )} (${getSignedPercentString(median.percent)}).`
                : "";
            const priceM2Answer = `W ${reportPeriod.month} ${reportPeriod.year} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. To ${avgPriceM2Difference}% ${isLowerPriceM2 ? "mniej" : "więcej"} niż w ubiegłym roku.${medianAnswerPart}`;

            if (FaqCities.includes(regionSlug)) {
                additionalFaqsArray.push(
                    {
                        name: `Która dzielnica ${regionWhere} jest najtańsza?`,
                        answer: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`,
                        answer_text: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`
                    },
                    {
                        name: `Jakie dzielnice są najdroższe ${regionWhere}?`,
                        answer: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`,
                        answer_text: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`
                    }
                );
            }

            const commonFaqsArray: IFaqQuestion[] = [
                {
                    name: `Ile kosztuje metr kwadratowy mieszkania ${regionWhere}?`,
                    answer: priceM2Answer,
                    answer_text: priceM2Answer
                },
                {
                    name: `Ile kosztuje mieszkanie ${regionWhere}?`,
                    answer: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`,
                    answer_text: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`
                }
            ];

            setAdditionalFaqs([...additionalFaqsArray, ...commonFaqsArray]);
        },
        [latestPriceReport, yearAgoPrices]
    );

    useEffect(() => {
        getLocalGenericFaq(minDistrict, maxDistrict, region);
    }, [minDistrict, maxDistrict, region]);

    return (
        <>
            {((faq && faq.length > 0) || additionalFaqs) && (
                <Container className={containerStyle}>
                    <FaqSection faqContent={faq} genericQuestions={additionalFaqs} className={faqStyle} />
                </Container>
            )}
        </>
    );
};

const containerStyle = css({
    marginBottom: 8
});

const faqStyle = css({
    "& h2": {
        textAlign: "center"
    },
    md: {
        width: "100%",
        maxWidth: "100%"
    }
});
